import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { enhancedServices } from "../../services/enhancedCrudService";

const TileSizes = () => {
  const [tileSizes, setTileSizes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typeCount, setTypeCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [editingSize, setEditingSize] = useState(null);
  const [formData, setFormData] = useState({ name: "" });
  const [deleteModal, setDeleteModal] = useState(false);
  const [deletingSize, setDeletingSize] = useState(null);
  const [undoDeprecationModal, setUndoDeprecationModal] = useState(false);
  const [undoDeprecatingSize, setUndoDeprecatingSize] = useState(null);
  const [linkedProducts, setLinkedProducts] = useState([]);
  const [forceSoftDelete, setForceSoftDelete] = useState(false);
  const [warningModal, setWarningModal] = useState(false);

  const fetchTileSizes = async () => {
    setLoading(true);
    try {
      const response = await enhancedServices.tile.getSizes();
      if (response.types) {
        setTileSizes(response.types || []);
        setTypeCount(response.count || 0);
      } else {
        toast.error(response.message || "Failed to fetch tile sizes");
      }
    } catch (error) {
      console.error("Error fetching tile sizes:", error);
      toast.error("Failed to fetch tile sizes");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const openAddModal = () => {
    setModalMode("add");
    setFormData({ name: "" });
    setEditingSize(null);
    setShowModal(true);
  };

  const openEditModal = (size) => {
    setModalMode("edit");
    setFormData({ name: size.name });
    setEditingSize(size);
    setShowModal(true);
  };

  const openDeleteModal = (size) => {
    setDeletingSize(size);
    setDeleteModal(true);
  };

  const openUndoDeprecationModal = (size) => {
    setUndoDeprecatingSize(size);
    setUndoDeprecationModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Name is required");
      return;
    }
    try {
      if (modalMode === "add") {
        const response = await enhancedServices.tile.createSize(formData);
        if (response.id) {
          toast.success(response.message || "Tile size created successfully");
          setShowModal(false);
          fetchTileSizes();
        } else {
          toast.error(response.message || "Failed to create tile size");
        }
      } else {
        const response = await enhancedServices.tile.updateSize(
          editingSize.id,
          formData
        );
        if (response) {
          toast.success(response.message || "Tile size updated successfully");
          setShowModal(false);
          fetchTileSizes();
        } else {
          toast.error(response.message || "Failed to update tile size");
        }
      }
    } catch (error) {
      console.error("Error saving tile size:", error);
      toast.error("Failed to save tile size");
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingSize) return;
    try {
      const response = await enhancedServices.tile.deleteSize(deletingSize.id);
      if (
        response &&
        response.linked_product_ids &&
        response.linked_product_ids.length > 0
      ) {
        setLinkedProducts(response.linked_product_ids);
        setDeleteModal(false);
        setWarningModal(true);
      } else {
        const softDeleteResponse = await enhancedServices.tile.deleteSize(
          deletingSize.id,
          { force_soft_delete: true }
        );
        if (softDeleteResponse) {
          toast.success(
            softDeleteResponse.message || "Tile size soft deleted successfully"
          );
        } else {
          toast.error("Failed to soft delete tile size");
        }
        setDeleteModal(false);
        setDeletingSize(null);
        fetchTileSizes();
      }
    } catch (error) {
      console.error("Error deleting tile size:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to delete tile size");
      }
      setDeleteModal(false);
      setDeletingSize(null);
    }
  };

  const handleDeleteWithForceSoftDelete = async () => {
    if (!deletingSize) return;

    try {
      const deleteData = {
        force_soft_delete: true,
      };
      const response = await enhancedServices.tile.deleteSize(
        deletingSize.id,
        deleteData
      );

      if (response) {
        // Handle different response scenarios
        if (response.status === "soft_deleted") {
          toast.success(
            response.message || "Size marked as deprecated successfully"
          );
        } else if (response.status === "deleted") {
          toast.success(response.message || "Tile size deleted successfully");
        } else {
          toast.success(response.message || "Tile size deleted successfully");
        }

        setWarningModal(false);
        setDeletingSize(null);
        setLinkedProducts([]);
        setForceSoftDelete(false);
        fetchTileSizes();
      } else {
        toast.error(response.message || "Failed to delete tile size");
      }
    } catch (error) {
      console.error("Error deleting tile size:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to delete tile size");
      }
    }
  };

  const handleUndoDeprecation = async () => {
    if (!undoDeprecatingSize) return;
    try {
      const response = await enhancedServices.tile.undoDeprecationSize(
        undoDeprecatingSize.id
      );
      if (response) {
        toast.success(response.message || "Deprecation undone successfully");
        setUndoDeprecationModal(false);
        setUndoDeprecatingSize(null);
        fetchTileSizes();
      } else {
        toast.error(response.message || "Failed to undo deprecation");
      }
    } catch (error) {
      console.error("Error undoing deprecation:", error);
      toast.error("Failed to undo deprecation");
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setFormData({ name: "" });
    setEditingSize(null);
  };

  const closeDeleteModal = () => {
    setDeleteModal(false);
    setDeletingSize(null);
  };

  const closeWarningModal = () => {
    setWarningModal(false);
    setDeletingSize(null);
    setLinkedProducts([]);
    setForceSoftDelete(false);
  };

  const closeUndoDeprecationModal = () => {
    setUndoDeprecationModal(false);
    setUndoDeprecatingSize(null);
  };

  useEffect(() => {
    fetchTileSizes();
  }, []);

  return (
    <>
      <div className="page-header mb-3">
        <div className="row align-items-center">
          <div className="col">
            <h3 className="page-title">Tile Sizes</h3>
          </div>
          <div className="col-auto text-end float-end ms-auto">
            <button className="btn btn-primary" onClick={openAddModal}>
              <i className="fe fe-plus"></i> Add New Size
            </button>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">Sizes</h5>
              <span className="badge bg-primary">Total: {typeCount}</span>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-hover">
                    <thead>
                      <tr>
                        <th>Sr.No</th>
                        <th>Name</th>
                        <th>Deprecated</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tileSizes.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="text-center">
                            No tile sizes found
                          </td>
                        </tr>
                      ) : (
                        tileSizes.map((size,index) => (
                            <tr key={index}>
                            <td>{index + 1}</td>
                            <td>{size.name}</td>
                            <td>{size.is_deprecated ? "Yes" : "No"}</td>
                            <td>
                              <div className="actions">
                                <button
                                  className="btn btn-sm btn-outline-primary me-2"
                                  onClick={() => openEditModal(size)}
                                  title="Edit"
                                >
                                  <i className="fe fe-edit"></i>
                                </button>
                                {size.is_deprecated && (
                                  <button
                                    className="btn btn-sm btn-outline-warning me-2"
                                    onClick={() =>
                                      openUndoDeprecationModal(size)
                                    }
                                    title="Undo Deprecation"
                                  >
                                    <i className="fe fe-rotate-ccw"></i>
                                  </button>
                                )}
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => openDeleteModal(size)}
                                  title="Delete"
                                >
                                  <i className="fe fe-trash-2"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {showModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {modalMode === "add" ? "Add New Tile Size" : "Edit Tile Size"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModal}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="name" className="form-label">
                      Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter tile size name"
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary me-2"
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {modalMode === "add" ? "Create" : "Update"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {deleteModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeDeleteModal}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to delete the tile size "
                  <strong>{deletingSize?.name}</strong>"?
                </p>
                <p className="text-danger">This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={closeDeleteModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteConfirm}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {warningModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm soft Delete</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeWarningModal}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning">
                  <i className="fe fe-alert-triangle me-2"></i>
                  <strong>Warning:</strong> The tile size "
                  <strong>{deletingSize?.name}</strong>" is linked to{" "}
                  {linkedProducts.length} product(s).
                </div>
                <p className="text-danger">
                  Are you sure you want to soft delete the tile size "
                  <strong>{deletingSize?.name}</strong>"?
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={closeWarningModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => {
                    handleDeleteWithForceSoftDelete();
                  }}
                >
                  Soft Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {undoDeprecationModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Undo Deprecation</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeUndoDeprecationModal}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to undo the deprecation for "
                  <strong>{undoDeprecatingSize?.name}</strong>"?
                </p>
                <p className="text-warning">
                  This will make the size active again and available for use.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={closeUndoDeprecationModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={handleUndoDeprecation}
                >
                  Undo Deprecation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {(showModal || deleteModal || warningModal || undoDeprecationModal) && (
        <div className="modal-backdrop fade show"></div>
      )}
    </>
  );
};

export default TileSizes;
